import { notification } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Button } from "@rumbledirect/ui";
import type { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import { generateDamageIntakeReport } from "@/lib/api/resources/dossiers";

interface GenerateDamageIntakeReportActionProps {
  dossier: Dossier | undefined;
}

/**
 * Generates the damage intake report PDF of a dossier. The PDF is stored on
 * the dossier, so it also shows up with the other files once the dossier is
 * refetched; the notification links to it directly.
 */
export const GenerateDamageIntakeReportAction = ({
  dossier,
}: GenerateDamageIntakeReportActionProps) => {
  const queryClient = useQueryClient();

  const generateMutation = useMutation({
    mutationFn: () => {
      if (!dossier) {
        throw new Error("No dossier provided");
      }

      return generateDamageIntakeReport(dossier.id);
    },
    onSuccess: (report) => {
      notification.success({
        message: "Schade-intake gegenereerd",
        description: (
          <a href={report.contentUrl} target="_blank" rel="noopener noreferrer">
            PDF openen
          </a>
        ),
      });
      if (dossier) {
        queryClient.invalidateQueries({ queryKey: ["dossier", dossier.id] });
      }
    },
    onError: (error) => {
      notification.error({
        message: "Genereren mislukt",
        description:
          isAxiosError(error) && error.response?.status === 422
            ? "Voor deze organisatie is geen schade-intake-sjabloon ingesteld."
            : "Er ging iets mis bij het genereren van de schade-intake.",
      });
    },
  });

  return (
    <Button
      size="large"
      className="w-full"
      disabled={!dossier}
      loading={generateMutation.isPending}
      onClick={() => generateMutation.mutate()}
    >
      Schade-intake PDF genereren
    </Button>
  );
};
